import { ErrorModal } from "@dexteel/mesf-core";
import {
	Box,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useState } from "react";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { CampaignResponse } from "@/pages/mill/schedule/models/CampaignReponse";
import { ELEMENTS, formatElementValue } from "../../report/hooks/hooks";
import { ChemistryRow } from "../../report/models/ChemistryRow";
import { getChemistryResults } from "../../report/repositories/ChemistriesReportRepository";

const InfoItem = ({ label, value }: { label: string; value: string }) => (
	<Stack direction="row" spacing={0.5} alignItems="baseline">
		<Typography variant="caption" color="text.secondary">
			{label}:
		</Typography>
		<Typography variant="caption" fontWeight={600}>
			{value}
		</Typography>
	</Stack>
);

export const useChemistryResults = (heatId: number | null) =>
	useQuery<ChemistryRow[], Error>({
		queryKey: ["chemistry-results", heatId],
		queryFn: ({ signal }) => getChemistryResults({ heatId }, signal),
		enabled: heatId != null,
	});

const getChemistryElements = (chemistry: ChemistryRow | null) =>
	ELEMENTS.map((symbol) => ({
		symbol,
		value: chemistry
			? formatElementValue(
					chemistry[symbol as keyof ChemistryRow],
					symbol,
					chemistry.BelowDetection,
				)
			: "",
	}));

export const hasChemistryValues = (chemistry: ChemistryRow | null) =>
	getChemistryElements(chemistry).some((element) => element.value !== "");

type Props = {
	heat: CampaignResponse | null;
};

export const ChemistryResultsContent = ({ heat }: Props) => {
	const [error, setError] = useState("");

	const {
		data: rows,
		isLoading,
		error: e,
		isError,
	} = useChemistryResults(heat?.HeatId ?? null);

	const chemistry = rows?.[0] ?? null;
	const elements = getChemistryElements(chemistry);

	const hasChemistry = elements.some((element) => element.value !== "");

	useEffect(() => {
		if (isError) setError(e.message);
	}, [isError, e]);

	return (
		<>
			{isLoading ? (
				<Box sx={{ py: 3 }}>
					<CenteredLazyLoading />
				</Box>
			) : hasChemistry ? (
				<>
					<Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap mb={1}>
						<InfoItem label="Supplier" value={chemistry?.Supplier ?? "-"} />
						<InfoItem
							label="External heat"
							value={chemistry?.ExternalHeatNumber ?? "-"}
						/>
						<InfoItem
							label="Reported"
							value={
								chemistry?.Reported
									? moment(chemistry.Reported).format("MM/DD/YY")
									: "-"
							}
						/>
						<InfoItem label="Spec" value={chemistry?.Spec ?? "-"} />
					</Stack>

					<TableContainer sx={{ width: "100%", overflowX: "auto" }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									{elements.map((element) => (
										<TableCell
											key={element.symbol}
											sx={{
												fontSize: 11,
												color: "text.secondary",
												textAlign: "right",
												py: 0.5,
											}}
										>
											{element.symbol}
										</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								<TableRow>
									{elements.map((element) => (
										<TableCell
											key={element.symbol}
											sx={{ fontSize: 11, textAlign: "right", py: 0.5 }}
										>
											{element.value === "" ? "-" : element.value}
										</TableCell>
									))}
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				</>
			) : (
				<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
					No chemistry loaded for this heat.
				</Typography>
			)}
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
